import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LeadForm } from "@/components/forms/LeadForm";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";

const pageContent = {
  installation: {
    eyebrow: "California solar installation",
    title: "Professional solar installation for California homes and commercial properties.",
    description: "Monarch designs solar systems around roof condition, power goals, long-term savings, and clean installation quality for California properties.",
    imageUrl: "https://images.pexels.com/photos/31912311/pexels-photo-31912311.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    sectionTitle: "Solar installation services designed for performance, aesthetics, and long-term value.",
    sectionDescription: "This page targets high-intent California installation searches with clearer service explanations and stronger local conversion copy.",
    highlights: [
      "Custom system sizing for real usage patterns",
      "Residential and commercial solar planning",
      "Roof-aware installation coordination",
      "Clean layouts that protect curb appeal",
      "Energy savings and monitoring guidance",
      "California-ready quote consultations",
    ],
  },
  repair: {
    eyebrow: "California solar repair",
    title: "Solar repair and maintenance support for underperforming California systems.",
    description: "From troubleshooting output drops to checking damaged panels, Monarch helps restore solar performance with practical repair-first guidance.",
    imageUrl: "https://images.pexels.com/photos/14614266/pexels-photo-14614266.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    sectionTitle: "Panel repair, inspections, and performance recovery for California solar owners.",
    sectionDescription: "These services support homeowners searching for fast solar repair, panel maintenance, and long-term system reliability in California.",
    highlights: [
      "Solar panel troubleshooting and inspections",
      "Monitoring issues and output drop analysis",
      "Maintenance planning for long-term efficiency",
      "Repair recommendations based on system condition",
      "Roof compatibility checks before upgrades",
      "Clear next steps for restore-or-replace decisions",
    ],
  },
  storage: {
    eyebrow: "California battery storage",
    title: "Battery storage solutions for backup power and stronger energy control.",
    description: "Monarch helps California property owners pair solar with battery storage for outage protection, peak-rate planning, and better energy resilience.",
    imageUrl: "https://images.unsplash.com/photo-1603827531753-0d72fb6a45e6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    sectionTitle: "Home battery and backup-ready energy planning for modern California properties.",
    sectionDescription: "This page gives search engines and visitors a dedicated destination for battery backup, backup power, and storage-related queries.",
    highlights: [
      "Backup power planning for outages",
      "Load shifting and energy resilience strategies",
      "Solar-plus-storage recommendations",
      "Homeowner guidance for battery sizing",
      "Peak-rate management support",
      "Long-term energy flexibility for California homes",
    ],
  },
  areas: {
    eyebrow: "California service areas",
    title: "Serving Los Angeles and nearby California communities with solar and roofing support.",
    description: "Monarch works with homeowners and commercial property owners across Los Angeles and nearby communities for solar, roofing, and energy storage projects.",
    imageUrl: "https://images.pexels.com/photos/14946525/pexels-photo-14946525.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    sectionTitle: "A stronger multi-page California SEO footprint for local solar and roofing searches.",
    sectionDescription: "This service-area page improves discoverability for California location terms while giving visitors a clear local-service message.",
    highlights: [
      "Los Angeles solar and roofing quotes",
      "Pasadena, Burbank, Encino, and Monrovia coverage",
      "Homeowner and commercial property support",
      "Fast quote response for nearby service areas",
      "Solar installation, roofing, storage, and inspections",
      "Local-first consultation and follow-up",
    ],
  },
};

export default function SolarBusinessPage({ pageKey, formId, buttonLabel, pageSource, relatedLinks }) {
  const page = pageContent[pageKey];

  return (
    <div className="space-y-24 pb-6" data-testid={`${pageKey}-business-page`}>
      <PageHero description={page.description} eyebrow={page.eyebrow} imageUrl={page.imageUrl} phone="800-281-2717" primaryHref={`#${formId}`} primaryLabel={buttonLabel} testIdPrefix={`${pageKey}-hero`} title={page.title} />

      <section className="px-4 md:px-6" data-testid={`${pageKey}-details-section`}>
        <div className="page-section space-y-10">
          <SectionHeading description={page.sectionDescription} eyebrow="SEO-ready service page" testIdPrefix={`${pageKey}-heading`} title={page.sectionTitle} />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {page.highlights.map((highlight) => (
              <Card className="rounded-[1.8rem] border-white/60 bg-white/92" data-testid={`${pageKey}-highlight-${highlight.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={highlight}>
                <CardContent className="flex items-start gap-3 p-5">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" />
                  <p className="text-sm text-foreground">{highlight}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="rounded-[2rem] bg-slate-950 p-6 text-white md:p-8" data-testid={`${pageKey}-cta-panel`}>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-300" data-testid={`${pageKey}-cta-eyebrow`}>
              California local conversion
            </p>
            <h2 className="mt-3 text-3xl font-bold" data-testid={`${pageKey}-cta-title`}>
              Make it easier for California visitors to find the exact service page they searched for.
            </h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {relatedLinks.map((item) => (
                <Button asChild className="rounded-full px-5 text-sm" key={item.to} variant="outline">
                  <Link data-testid={`${pageKey}-related-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} to={item.to}>
                    {item.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid={`${pageKey}-form-section`} id={formId}>
        <div className="page-section">
          <LeadForm buttonLabel={buttonLabel} description="Tell Monarch about your California property goals and we’ll guide the best next step." leadType="quote" pageSource={pageSource} testIdPrefix={`${pageKey}-quote`} title="Request your California quote" />
        </div>
      </section>
    </div>
  );
}